/**
 * Blok 89 — IntentRebuildHero — feature-flag gated draft.
 *
 * Doel: Powersure niet laten voelen als P1-meter/hardware-site maar als
 * maandelijkse energie-inzicht en optimalisatie-app.
 *
 * Default OFF in productie. Toggled via backend `/api/features/public`
 * field `landing_intent_rebuild_enabled` (env PS_LANDING_INTENT_REBUILD_ENABLED).
 *
 * Hard requirements:
 *  - geen Stripe/checkout/pricing wijziging — CTA blijft naar bestaande flow
 *  - geen claims over gegarandeerde besparing
 *  - geen device-control
 *  - geen marketingmail
 *  - duidelijke disambiguation tegen hardware-zoekers
 *  - prijs €9,95 eerste 3 maanden, daarna €14,95, opzegbaar
 */
import { useTranslation } from 'react-i18next';
import {
  Activity, BellRing, Calendar, AlertCircle, ShieldCheck, Zap,
} from 'lucide-react';
import { psCls } from '@/lib/powersure-theme';
import LandingPrimaryCta from '@/components/home/LandingPrimaryCta';

const VALUE_BULLETS = [
  {
    icon: Activity,
    title: 'Live inzicht via je bestaande slimme meter',
    body:
      'Geen losse meter te koop. Werkt met je bestaande P1-poort waar ondersteund. ' +
      'Zonder koppeling blijft inzicht beperkter.',
  },
  {
    icon: BellRing,
    title: 'Waarschuwingen bij pieken en teruglevermomenten',
    body:
      'Powersure ziet wanneer je veel teruglevert of een verbruikspiek hebt en ' +
      'geeft een seintje wat je kunt doen.',
  },
  {
    icon: Calendar,
    title: 'Maandelijkse optimalisatie en vervolgstappen',
    body:
      'Eén rapport per maand met concrete, persoonlijke vervolgstappen. ' +
      'Geen reclame. Geen verplichting tot iets bestellen.',
  },
];

export default function IntentRebuildHero({ onLanguageClick }) {
  useTranslation();

  return (
    <section
      className="relative py-10 sm:py-14 lg:py-20"
      style={{ background: 'linear-gradient(180deg, #0a1410 0%, #0e1b16 100%)' }}
      data-testid="hero-intent-rebuild-section"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* DISAMBIGUATION badge — bovenaan, klein, expliciet */}
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border mb-5"
            style={{
              background: 'rgba(16,185,129,0.08)',
              borderColor: 'rgba(16,185,129,0.35)',
              color: '#a7f3d0',
            }}
            data-testid="hero-disambiguation-badge"
          >
            <ShieldCheck size={14} />
            Geen P1-meter webshop · maandelijks energie-inzicht abonnement
          </div>

          {/* H1 — service-intent */}
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-4"
            style={{ color: '#f8fafc' }}
            data-testid="hero-intent-rebuild-h1"
          >
            Maandelijks energie-inzicht abonnement.
          </h1>

          {/* Lead — wat het IS, wat het NIET is */}
          <p
            className="text-base sm:text-lg leading-relaxed mb-6"
            style={{ color: '#cbd5e1' }}
            data-testid="hero-intent-rebuild-lead"
          >
            Powersure is geen losse P1-meter webshop. Powersure is een
            maandelijkse <strong>energie-inzicht en optimalisatie-app</strong>{' '}
            die werkt met je bestaande slimme meter waar dat kan.
          </p>

          {/* Prijs — duidelijk boven de fold */}
          <div
            className="inline-flex items-center gap-3 px-4 py-3 rounded-xl border mb-7"
            style={{
              background: 'rgba(255,255,255,0.04)',
              borderColor: 'rgba(255,255,255,0.10)',
              color: '#f8fafc',
            }}
            data-testid="hero-intent-rebuild-price"
          >
            <Zap size={20} style={{ color: '#34d399' }} />
            <div className="text-sm leading-snug">
              <div className="font-semibold">
                € 9,95 per maand de eerste 3 maanden
              </div>
              <div className="text-xs" style={{ color: '#94a3b8' }}>
                Daarna € 14,95 per maand. Opzegbaar.
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mb-9">
            <LandingPrimaryCta
              label="Start Powersure voor € 9,95/mnd"
              dataTestId="hero-intent-rebuild-cta"
            />
          </div>

          {/* Hardware-filter notice */}
          <div
            className="flex items-start gap-2 text-xs mb-10"
            style={{ color: '#94a3b8' }}
            data-testid="hero-intent-rebuild-hardware-filter"
          >
            <AlertCircle size={14} style={{ marginTop: 2, flexShrink: 0 }} />
            <span>
              Geen losse P1-meter kopen via Powersure. We koppelen waar
              mogelijk met je bestaande slimme meter — zonder koppeling
              blijft het inzicht beperkter.
            </span>
          </div>

          {/* Value bullets — wat krijg je */}
          <div className="grid sm:grid-cols-1 gap-4">
            {VALUE_BULLETS.map((b) => (
              <div
                key={b.title}
                className={psCls(
                  'flex items-start gap-3 p-4 rounded-xl border',
                )}
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  borderColor: 'rgba(255,255,255,0.08)',
                }}
                data-testid={`hero-intent-rebuild-bullet-${b.title.slice(0, 12).toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div
                  className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{
                    background: 'rgba(16,185,129,0.12)',
                    color: '#34d399',
                  }}
                >
                  <b.icon size={18} />
                </div>
                <div>
                  <div
                    className="text-sm font-semibold mb-1"
                    style={{ color: '#f8fafc' }}
                  >
                    {b.title}
                  </div>
                  <div className="text-xs leading-relaxed" style={{ color: '#cbd5e1' }}>
                    {b.body}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
